import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { Admin, type OutboxEvent, type OutboxEventStatus, type OutboxChannel, type OutboxEventType } from "../lib/api";

export default function AdminOutbox() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // States
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<OutboxEvent[]>([]);
  const [stats, setStats] = useState<{
    byStatus: { queued: number; sending: number; delivered: number; failed: number };
    byChannel: { email: number; sse: number; push: number };
    oldestQueuedAge: number | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<OutboxEventStatus | "all">("all");
  const [channelFilter, setChannelFilter] = useState<OutboxChannel | "all">("all");
  const [typeFilter, setTypeFilter] = useState<OutboxEventType | "all">("all");

  // Detail panel
  const [selectedItem, setSelectedItem] = useState<OutboxEvent | null>(null);
  const [retryBusy, setRetryBusy] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [eventsRes, statsRes] = await Promise.all([
        Admin.outboxList({ limit: 200, offset: 0 }),
        Admin.outboxStats(),
      ]);
      setEvents(eventsRes.events);
      setStats(statsRes.stats);
    } catch (e: any) {
      console.error("AdminOutbox load failed:", e);
      setError(e?.message || "Outbox verileri yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }

  async function handleRetry(id: string) {
    if (retryBusy) return;
    setRetryBusy(true);
    setRetryError(null);
    try {
      await Admin.outboxRetry(id);
      alert("Event tekrar gönderilmek üzere sıraya alındı!");
      await load(); // Refresh list
      // Update selected item if it's the one we retried
      if (selectedItem?._id === id) {
        const updated = events.find((e) => e._id === id);
        if (updated) setSelectedItem(updated);
      }
    } catch (e: any) {
      console.error("Retry failed:", e);
      setRetryError(e?.message || "Tekrar gönderme başarısız oldu.");
    } finally {
      setRetryBusy(false);
    }
  }

  // Client-side filtering
  const filteredEvents = useMemo(() => {
    let result = events;

    // Status filter
    if (statusFilter !== "all") {
      result = result.filter((e) => e.status === statusFilter);
    }

    // Channel filter
    if (channelFilter !== "all") {
      result = result.filter((e) => e.channel === channelFilter);
    }

    // Type filter
    if (typeFilter !== "all") {
      result = result.filter((e) => e.type === typeFilter);
    }

    // Search query (search in: id, type, channel, status, payload string, meta)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((e) => {
        const idMatch = e._id.toLowerCase().includes(q);
        const typeMatch = e.type.toLowerCase().includes(q);
        const channelMatch = e.channel.toLowerCase().includes(q);
        const statusMatch = e.status.toLowerCase().includes(q);
        const payloadStr = JSON.stringify(e.payload).toLowerCase();
        const payloadMatch = payloadStr.includes(q);
        const metaStr = e.meta ? JSON.stringify(e.meta).toLowerCase() : "";
        const metaMatch = metaStr.includes(q);
        return idMatch || typeMatch || channelMatch || statusMatch || payloadMatch || metaMatch;
      });
    }

    return result;
  }, [events, searchQuery, statusFilter, channelFilter, typeFilter]);

  // Check if filters are active
  const hasActiveFilters = searchQuery.trim() || statusFilter !== "all" || channelFilter !== "all" || typeFilter !== "all";

  function clearFilters() {
    setSearchQuery("");
    setStatusFilter("all");
    setChannelFilter("all");
    setTypeFilter("all");
  }

  function getStatusBadgeStyle(status: OutboxEventStatus) {
    switch (status) {
      case "queued":
        return { backgroundColor: "rgba(234, 179, 8, 0.15)", color: "#eab308", border: "1px solid rgba(234, 179, 8, 0.4)" };
      case "sending":
        return { backgroundColor: "rgba(59, 130, 246, 0.15)", color: "#3b82f6", border: "1px solid rgba(59, 130, 246, 0.4)" };
      case "delivered":
        return { backgroundColor: "rgba(34, 197, 94, 0.15)", color: "#22c55e", border: "1px solid rgba(34, 197, 94, 0.4)" };
      case "failed":
        return { backgroundColor: "rgba(239, 68, 68, 0.15)", color: "#ef4444", border: "1px solid rgba(239, 68, 68, 0.4)" };
      default:
        return { backgroundColor: "rgba(156, 163, 175, 0.15)", color: "#9ca3af", border: "1px solid rgba(156, 163, 175, 0.4)" };
    }
  }

  function getTypeBadgeStyle(type: OutboxEventType) {
    switch (type) {
      case "email":
        return { backgroundColor: "rgba(139, 92, 246, 0.15)", color: "#8b5cf6", border: "1px solid rgba(139, 92, 246, 0.4)" };
      case "sse":
        return { backgroundColor: "rgba(59, 130, 246, 0.15)", color: "#3b82f6", border: "1px solid rgba(59, 130, 246, 0.4)" };
      case "push":
        return { backgroundColor: "rgba(236, 72, 153, 0.15)", color: "#ec4899", border: "1px solid rgba(236, 72, 153, 0.4)" };
      case "savwar_leaderboard":
        return { backgroundColor: "rgba(251, 191, 36, 0.15)", color: "#fbbf24", border: "1px solid rgba(251, 191, 36, 0.4)" };
      case "savlike_winner":
        return { backgroundColor: "rgba(34, 197, 94, 0.15)", color: "#22c55e", border: "1px solid rgba(34, 197, 94, 0.4)" };
      default:
        return { backgroundColor: "rgba(156, 163, 175, 0.15)", color: "#9ca3af", border: "1px solid rgba(156, 163, 175, 0.4)" };
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check admin permission
  if (!user?.isAdmin) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <h1>🚫 Erişim Engellendi</h1>
        <p>Bu sayfa için admin yetkisi gerekli.</p>
      </div>
    );
  }

  function shortenId(id: string) {
    if (id.length <= 12) return id;
    return `${id.slice(0, 6)}...${id.slice(-4)}`;
  }

  function formatDate(dateStr?: string | null) {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleString("tr-TR");
    } catch {
      return dateStr;
    }
  }

  function getRowBgColor(status: OutboxEventStatus) {
    switch (status) {
      case "failed":
        return "rgba(239, 68, 68, 0.08)";
      case "queued":
        return "rgba(234, 179, 8, 0.05)";
      default:
        return "transparent";
    }
  }

  // Loading state
  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
        <p style={{ color: "var(--muted)" }}>Outbox verileri yükleniyor...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1600, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, color: "var(--text)" }}>
          Admin · Outbox
        </h1>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={() => load()}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 16px",
              fontSize: 14,
              fontWeight: 600,
              borderRadius: 8,
              border: "1px solid rgba(59, 130, 246, 0.4)",
              backgroundColor: "rgba(59, 130, 246, 0.1)",
              color: "#3b82f6",
              cursor: "pointer",
            }}
            title="Listeyi yenile"
          >
            <span>🔄</span>
            <span>Yenile</span>
          </button>
          <button
            onClick={() => navigate("/settings")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 16px",
              fontSize: 14,
              fontWeight: 600,
              borderRadius: 8,
              border: "1px solid rgba(156, 163, 175, 0.4)",
              backgroundColor: "rgba(156, 163, 175, 0.1)",
              color: "#9ca3af",
              cursor: "pointer",
            }}
            title="Ayarlara dön"
          >
            <span>←</span>
            <span>Geri</span>
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div
          style={{
            padding: 16,
            marginBottom: 16,
            borderRadius: 10,
            backgroundColor: "rgba(239, 68, 68, 0.1)",
            border: "1px solid rgba(239, 68, 68, 0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 20 }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 700, color: "#ef4444" }}>Outbox verileri alınamadı</div>
              <div style={{ fontSize: 14, color: "#ef4444", opacity: 0.9 }}>{error}</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => load()}
              style={{
                padding: "6px 12px",
                fontSize: 13,
                fontWeight: 600,
                borderRadius: 6,
                border: "1px solid rgba(239, 68, 68, 0.4)",
                backgroundColor: "rgba(239, 68, 68, 0.15)",
                color: "#ef4444",
                cursor: "pointer",
              }}
            >
              🔄 Yeniden Dene
            </button>
            <button
              onClick={() => setError(null)}
              style={{
                padding: "6px 12px",
                fontSize: 13,
                fontWeight: 600,
                borderRadius: 6,
                border: "1px solid rgba(156, 163, 175, 0.4)",
                backgroundColor: "rgba(156, 163, 175, 0.1)",
                color: "#9ca3af",
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Stats cards */}
      {stats && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 16,
            marginBottom: 24,
          }}
        >
          <div
            style={{
              padding: 16,
              borderRadius: 10,
              border: "1px solid rgba(234, 179, 8, 0.4)",
              backgroundColor: "rgba(234, 179, 8, 0.08)",
            }}
          >
            <div style={{ fontSize: 24, fontWeight: 800, color: "#eab308" }}>{stats.byStatus.queued}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#eab308", opacity: 0.8 }}>Sırada</div>
          </div>
          <div
            style={{
              padding: 16,
              borderRadius: 10,
              border: "1px solid rgba(59, 130, 246, 0.4)",
              backgroundColor: "rgba(59, 130, 246, 0.08)",
            }}
          >
            <div style={{ fontSize: 24, fontWeight: 800, color: "#3b82f6" }}>{stats.byStatus.sending}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#3b82f6", opacity: 0.8 }}>Gönderiliyor</div>
          </div>
          <div
            style={{
              padding: 16,
              borderRadius: 10,
              border: "1px solid rgba(34, 197, 94, 0.4)",
              backgroundColor: "rgba(34, 197, 94, 0.08)",
            }}
          >
            <div style={{ fontSize: 24, fontWeight: 800, color: "#22c55e" }}>{stats.byStatus.delivered}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#22c55e", opacity: 0.8 }}>Teslim Edildi</div>
          </div>
          <div
            style={{
              padding: 16,
              borderRadius: 10,
              border: "1px solid rgba(239, 68, 68, 0.4)",
              backgroundColor: "rgba(239, 68, 68, 0.08)",
            }}
          >
            <div style={{ fontSize: 24, fontWeight: 800, color: "#ef4444" }}>{stats.byStatus.failed}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#ef4444", opacity: 0.8 }}>Başarısız</div>
          </div>
        </div>
      )}

      {/* Main layout: Left list + Right detail */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1.2fr)",
          gap: 16,
        }}
      >
        {/* Left: List + Filters */}
        <div>
          {/* Filters */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
              <input
                type="text"
                placeholder="🔍 Ara (ID, type, channel, payload...)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  flex: "1 1 200px",
                  padding: "10px 12px",
                  fontSize: 14,
                  borderRadius: 8,
                  border: "1px solid rgba(156, 163, 175, 0.3)",
                  backgroundColor: "var(--bg2)",
                  color: "var(--text)",
                }}
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                style={{
                  padding: "10px 12px",
                  fontSize: 14,
                  borderRadius: 8,
                  border: "1px solid rgba(156, 163, 175, 0.3)",
                  backgroundColor: "var(--bg2)",
                  color: "var(--text)",
                  cursor: "pointer",
                }}
              >
                <option value="all">Tüm Durumlar</option>
                <option value="queued">Queued</option>
                <option value="sending">Sending</option>
                <option value="delivered">Delivered</option>
                <option value="failed">Failed</option>
              </select>
              <select
                value={channelFilter}
                onChange={(e) => setChannelFilter(e.target.value as any)}
                style={{
                  padding: "10px 12px",
                  fontSize: 14,
                  borderRadius: 8,
                  border: "1px solid rgba(156, 163, 175, 0.3)",
                  backgroundColor: "var(--bg2)",
                  color: "var(--text)",
                  cursor: "pointer",
                }}
              >
                <option value="all">Tüm Kanallar</option>
                <option value="email">Email</option>
                <option value="sse">SSE</option>
                <option value="push">Push</option>
              </select>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as any)}
                style={{
                  padding: "10px 12px",
                  fontSize: 14,
                  borderRadius: 8,
                  border: "1px solid rgba(156, 163, 175, 0.3)",
                  backgroundColor: "var(--bg2)",
                  color: "var(--text)",
                  cursor: "pointer",
                }}
              >
                <option value="all">Tüm Tipler</option>
                <option value="email">Email</option>
                <option value="sse">SSE</option>
                <option value="push">Push</option>
                <option value="savwar_leaderboard">SavWar Leaderboard</option>
                <option value="savlike_winner">SavLike Winner</option>
              </select>
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  fontSize: 13,
                  fontWeight: 600,
                  borderRadius: 6,
                  border: "1px solid rgba(156, 163, 175, 0.4)",
                  backgroundColor: "rgba(156, 163, 175, 0.1)",
                  color: "#9ca3af",
                  cursor: "pointer",
                }}
              >
                <span>🗑️</span>
                <span>Filtreleri Temizle</span>
              </button>
            )}
          </div>

          {/* Table */}
          <div
            style={{
              overflowX: "auto",
              borderRadius: 10,
              border: "1px solid rgba(156, 163, 175, 0.2)",
              backgroundColor: "var(--bg2)",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 14,
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "rgba(156, 163, 175, 0.08)" }}>
                  <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)", whiteSpace: "nowrap" }}>ID</th>
                  <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)", whiteSpace: "nowrap" }}>Type</th>
                  <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)", whiteSpace: "nowrap" }}>Status</th>
                  <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)", whiteSpace: "nowrap" }}>Channel</th>
                  <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)", whiteSpace: "nowrap" }}>Attempts</th>
                  <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)", whiteSpace: "nowrap" }}>Created</th>
                  <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)", whiteSpace: "nowrap" }}>Delivered</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: 24, textAlign: "center", color: "var(--muted)" }}>
                      {hasActiveFilters ? "Filtre sonucu bulunamadı" : "Henüz outbox kaydı yok"}
                    </td>
                  </tr>
                )}
                {filteredEvents.map((event) => (
                  <tr
                    key={event._id}
                    onClick={() => setSelectedItem(event)}
                    style={{
                      cursor: "pointer",
                      backgroundColor: selectedItem?._id === event._id ? "rgba(59, 130, 246, 0.12)" : getRowBgColor(event.status),
                      borderBottom: "1px solid rgba(156, 163, 175, 0.1)",
                    }}
                    onMouseEnter={(e) => {
                      if (selectedItem?._id !== event._id) {
                        e.currentTarget.style.backgroundColor = "rgba(156, 163, 175, 0.05)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedItem?._id !== event._id) {
                        e.currentTarget.style.backgroundColor = getRowBgColor(event.status);
                      }
                    }}
                  >
                    <td style={{ padding: 12, fontFamily: "monospace", fontSize: 12, color: "var(--text)" }}>{shortenId(event._id)}</td>
                    <td style={{ padding: 12 }}>
                      <span
                        style={{
                          ...getTypeBadgeStyle(event.type),
                          padding: "4px 8px",
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {event.type}
                      </span>
                    </td>
                    <td style={{ padding: 12 }}>
                      <span
                        style={{
                          ...getStatusBadgeStyle(event.status),
                          padding: "4px 8px",
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {event.status}
                      </span>
                    </td>
                    <td style={{ padding: 12, color: "var(--muted)", whiteSpace: "nowrap" }}>{event.channel}</td>
                    <td style={{ padding: 12, color: "var(--text)", whiteSpace: "nowrap" }}>
                      {event.attempts} / {event.maxAttempts}
                    </td>
                    <td style={{ padding: 12, color: "var(--muted)", fontSize: 12, whiteSpace: "nowrap" }}>{formatDate(event.createdAt)}</td>
                    <td style={{ padding: 12, color: "var(--muted)", fontSize: 12, whiteSpace: "nowrap" }}>{formatDate(event.deliveredAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right: Detail Panel */}
        <div
          style={{
            borderRadius: 10,
            border: "1px solid rgba(156, 163, 175, 0.2)",
            backgroundColor: "var(--bg2)",
            padding: 20,
            height: "fit-content",
            position: "sticky",
            top: 24,
          }}
        >
          {!selectedItem ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--muted)" }}>
              <div style={{ fontSize: 64, marginBottom: 16 }}>📨</div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Sol taraftan bir outbox kaydı seç</div>
              <div style={{ fontSize: 14, marginTop: 8, opacity: 0.7 }}>Detaylı bilgiler burada görünecek</div>
            </div>
          ) : (
            <div>
              {/* Header */}
              <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid rgba(156, 163, 175, 0.2)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, color: "var(--text)" }}>Outbox Event</h2>
                  <span
                    style={{
                      ...getStatusBadgeStyle(selectedItem.status),
                      padding: "4px 10px",
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {selectedItem.status}
                  </span>
                </div>
                <div style={{ fontSize: 11, fontFamily: "monospace", color: "var(--muted)", wordBreak: "break-all" }}>
                  {selectedItem._id}
                </div>
              </div>

              {/* Basic Info */}
              <section style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: "var(--text)" }}>📋 Temel Bilgiler</h3>
                <div style={{ display: "grid", gap: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 13, color: "var(--muted)" }}>Type:</span>
                    <span
                      style={{
                        ...getTypeBadgeStyle(selectedItem.type),
                        padding: "4px 10px",
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {selectedItem.type}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 13, color: "var(--muted)" }}>Channel:</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{selectedItem.channel}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 13, color: "var(--muted)" }}>Attempts:</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>
                      {selectedItem.attempts} / {selectedItem.maxAttempts}
                    </span>
                  </div>
                  {selectedItem.retryCount !== undefined && selectedItem.retryCount > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>Manual Retries:</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#eab308" }}>{selectedItem.retryCount}</span>
                    </div>
                  )}
                  {selectedItem.dedupKey && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>Dedup Key:</span>
                      <span
                        style={{ fontSize: 11, fontFamily: "monospace", color: "var(--text)", wordBreak: "break-all", textAlign: "right", maxWidth: "60%" }}
                      >
                        {selectedItem.dedupKey}
                      </span>
                    </div>
                  )}
                </div>
              </section>

              {/* Timing */}
              <section style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: "var(--text)" }}>⏰ Zamanlar</h3>
                <div style={{ display: "grid", gap: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 13, color: "var(--muted)" }}>Created:</span>
                    <span style={{ fontSize: 12, color: "var(--text)" }}>{formatDate(selectedItem.createdAt)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 13, color: "var(--muted)" }}>Updated:</span>
                    <span style={{ fontSize: 12, color: "var(--text)" }}>{formatDate(selectedItem.updatedAt)}</span>
                  </div>
                  {selectedItem.deliveredAt && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 13, color: "var(--muted)" }}>Delivered:</span>
                      <span style={{ fontSize: 12, color: "#22c55e", fontWeight: 600 }}>{formatDate(selectedItem.deliveredAt)}</span>
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 13, color: "var(--muted)" }}>Next Attempt:</span>
                    <span style={{ fontSize: 12, color: "var(--text)" }}>{formatDate(selectedItem.nextAttemptAt)}</span>
                  </div>
                </div>
              </section>

              {/* Error Message */}
              {(selectedItem.errorMessage || selectedItem.lastError) && (
                <section style={{ marginBottom: 20 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: "#ef4444" }}>⚠️ Hata Mesajı</h3>
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      backgroundColor: "rgba(239, 68, 68, 0.08)",
                      border: "1px solid rgba(239, 68, 68, 0.3)",
                      fontSize: 12,
                      fontFamily: "monospace",
                      color: "#ef4444",
                      wordBreak: "break-word",
                      maxHeight: 200,
                      overflowY: "auto",
                    }}
                  >
                    {selectedItem.lastError || selectedItem.errorMessage}
                  </div>
                </section>
              )}

              {/* Payload */}
              <section style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: "var(--text)" }}>📦 Payload</h3>
                <div
                  style={{
                    padding: 12,
                    borderRadius: 8,
                    backgroundColor: "rgba(156, 163, 175, 0.08)",
                    border: "1px solid rgba(156, 163, 175, 0.2)",
                    fontSize: 11,
                    fontFamily: "monospace",
                    color: "var(--text)",
                    wordBreak: "break-word",
                    maxHeight: 300,
                    overflowY: "auto",
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{JSON.stringify(selectedItem.payload, null, 2)}</pre>
                </div>
              </section>

              {/* Meta */}
              {selectedItem.meta && (
                <section style={{ marginBottom: 20 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: "var(--text)" }}>🔖 Meta</h3>
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      backgroundColor: "rgba(156, 163, 175, 0.08)",
                      border: "1px solid rgba(156, 163, 175, 0.2)",
                      fontSize: 11,
                      fontFamily: "monospace",
                      color: "var(--text)",
                      wordBreak: "break-word",
                      maxHeight: 200,
                      overflowY: "auto",
                    }}
                  >
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{JSON.stringify(selectedItem.meta, null, 2)}</pre>
                  </div>
                </section>
              )}

              {/* Retry error banner */}
              {retryError && (
                <div
                  style={{
                    padding: 12,
                    marginBottom: 16,
                    borderRadius: 8,
                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                    border: "1px solid rgba(239, 68, 68, 0.4)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <div style={{ fontSize: 13, color: "#ef4444", fontWeight: 600 }}>{retryError}</div>
                  <button
                    onClick={() => setRetryError(null)}
                    style={{
                      padding: "4px 8px",
                      fontSize: 12,
                      fontWeight: 600,
                      borderRadius: 4,
                      border: "1px solid rgba(239, 68, 68, 0.4)",
                      backgroundColor: "transparent",
                      color: "#ef4444",
                      cursor: "pointer",
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => handleRetry(selectedItem._id)}
                  disabled={retryBusy || (selectedItem.status !== "failed" && selectedItem.status !== "queued")}
                  style={{
                    flex: 1,
                    padding: "10px 16px",
                    fontSize: 14,
                    fontWeight: 700,
                    borderRadius: 8,
                    border: "1px solid rgba(34, 197, 94, 0.4)",
                    backgroundColor: selectedItem.status === "failed" || selectedItem.status === "queued" ? "rgba(34, 197, 94, 0.15)" : "rgba(156, 163, 175, 0.08)",
                    color: selectedItem.status === "failed" || selectedItem.status === "queued" ? "#22c55e" : "#9ca3af",
                    cursor: selectedItem.status === "failed" || selectedItem.status === "queued" ? "pointer" : "not-allowed",
                    opacity: retryBusy ? 0.6 : 1,
                  }}
                  title={
                    selectedItem.status === "delivered"
                      ? "Teslim edilmiş event retry edilemez"
                      : selectedItem.status === "sending"
                      ? "Gönderilmekte olan event retry edilemez"
                      : "Event'i tekrar gönder"
                  }
                >
                  {retryBusy ? "⏳ Retry Ediliyor..." : "🔄 Tekrar Gönder"}
                </button>
                <button
                  onClick={() => setSelectedItem(null)}
                  style={{
                    padding: "10px 16px",
                    fontSize: 14,
                    fontWeight: 700,
                    borderRadius: 8,
                    border: "1px solid rgba(156, 163, 175, 0.4)",
                    backgroundColor: "rgba(156, 163, 175, 0.1)",
                    color: "#9ca3af",
                    cursor: "pointer",
                  }}
                  title="Detay panelini kapat"
                >
                  Kapat
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
